import React, { useState, FormEvent } from 'react'
import { useRouter } from 'next/router'
import { Button } from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import {
  TrackClick,
  TrackPageViewed,
  TrackOnMount,
  TrackingAttributes,
  jarvisPageNames,
} from '../../tracking/jarvisTracking'
import styles from './LoginPage.module.scss'

const LoginPage = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const returnTo = (router.query.returnTo as string) || '/'

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    if (!email || !password) {
      setError(t('login.emailAndPasswordRequired'))
      setIsLoading(false)
      return
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError(t('login.invalidEmailFormat'))
      setIsLoading(false)
      return
    }

    try {
      const response = await fetch('/api/jarvis/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      })

      const data = await response.json()

      if (!response.ok) {
        setError(data.error || t('login.invalidCredentials'))
        setIsLoading(false)
        return
      }

      window.location.href = returnTo
    } catch (err) {
      setError(t('login.genericError'))
      setIsLoading(false)
    }
  }

  return (
    <TrackingAttributes page={jarvisPageNames.LOGIN}>
      <div
        className={`${styles.container} mc-p-4 d-flex align-items-center justify-content-center`}
      >
        <TrackPageViewed name={jarvisPageNames.LOGIN} product='jarvis' />
        <TrackOnMount event='jarvisViewed' name={jarvisPageNames.LOGIN} />
        <div
          className={`${styles.card} j-bg-white j-border-light mc-w-100 mc-py-6 mc-px-5 mc-corners--xl mc-mx-auto`}
        >
          <div className='mc-mb-5'>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src='https://static.masterclass.com/jarvis_higher_quality_tmp.png'
              alt='Jarvis'
              className={`${styles.logo} d-block mc-mx-auto`}
            />
          </div>

          <h1
            className={`${styles.title} j-text-color--dark mc-text-h3 mc-text--bold mc-text--center mc-mb-3`}
          >
            {t('login.title')}
          </h1>
          <p
            className={`${styles.subtitle} j-text-color--dark mc-text-small mc-text--normal mc-text--center`}
          >
            {t('login.subtitle')}
          </p>

          <TrackingAttributes location='login-form'>
            <form
              onSubmit={handleSubmit}
              className='d-flex flex-column mc-mt-5'
            >
              <div className='mc-mb-3'>
                <label
                  htmlFor='email'
                  className={`${styles.label} j-text-color--dark mc-text-x-small mc-mb-2 d-block`}
                >
                  {t('login.emailLabel')}
                </label>
                <input
                  id='email'
                  type='email'
                  placeholder={t('login.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`${styles.input} j-bg-white j-text-color--dark j-border-light mc-w-100 mc-py-3 mc-px-3 mc-corners--sm`}
                  disabled={isLoading}
                  autoComplete='email'
                />
              </div>

              <div className='mc-mb-3'>
                <label
                  htmlFor='password'
                  className={`${styles.label} j-text-color--dark mc-text-x-small mc-mb-2 d-block`}
                >
                  {t('login.passwordLabel')}
                </label>
                <input
                  id='password'
                  type='password'
                  placeholder={t('login.passwordPlaceholder')}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`${styles.input} j-bg-white j-text-color--dark j-border-light mc-w-100 mc-py-3 mc-px-3 mc-corners--sm`}
                  disabled={isLoading}
                  autoComplete='current-password'
                />
              </div>

              {error && (
                <div
                  className={`${styles.error} j-text-color--error mc-text-x-small mc-text--center mc-mb-3 mc-py-2 mc-px-3 mc-corners--sm`}
                  role='alert'
                >
                  {error}
                </div>
              )}

              <TrackClick event='jarvisClick' action='Continue'>
                <Button
                  type='submit'
                  kind='primary'
                  fullWidth
                  disabled={isLoading}
                  loading={isLoading}
                  className={!error ? 'mc-mt-3' : ''}
                >
                  {t('login.signInButton')}
                </Button>
              </TrackClick>
            </form>
          </TrackingAttributes>
        </div>
      </div>
    </TrackingAttributes>
  )
}

export { LoginPage }
